import type { TenantAiStatus } from '@avrahami/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState, type SyntheticEvent } from 'react';
import { keys, platformApi, tenantAiQuery } from '../api/platform';
import { he } from '../i18n/he';
import { formatCount, parseUsdLimit, usdLimitInput } from '../lib/ai';
import { errorMessage } from '../lib/format';
import { Alert, Button, buttonClass, Card, Loading, TextField } from '@avrahami/ui';
import { BudgetMeter, CopyProgress, LimitMark } from './AiMeters';

/**
 * The store's marketing copy: how many products have copy of their own, the engine's jobs, and
 * this month's spend against the store's limit. Staff who may manage the engine set the limit
 * here, and go from here to the screen of the store's copy.
 */
export function TenantAiCard({ tenantId, canManage }: { tenantId: string; canManage: boolean }) {
  const status = useQuery(tenantAiQuery(tenantId));
  return (
    <Card
      title={he.ai.card.title}
      actions={
        canManage ? (
          <Link
            to="/tenants/$tenantId/copy"
            params={{ tenantId }}
            className={buttonClass()}
            data-testid="tenant-copy-link"
          >
            {he.aiCopy.link}
          </Link>
        ) : null
      }
    >
      {status.isPending ? (
        <Loading />
      ) : status.isError ? (
        <Alert>
          {errorMessage(status.error, {
            FORBIDDEN: he.ai.viewForbidden,
            NOT_FOUND: he.ai.card.notFound,
          })}
        </Alert>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2" data-testid="tenant-ai">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <h3 className="text-sm font-medium text-slate-700">{he.ai.card.products}</h3>
              <CopyProgress
                products={status.data.products}
                label={he.ai.card.productsMeter}
                testId="ai-copy-progress"
              />
            </div>
            <Jobs jobs={status.data.jobs} />
          </div>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <h3 className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-700">
                {he.ai.card.budget}
                <LimitMark isDefault={status.data.limitIsDefault} />
              </h3>
              <BudgetMeter
                spentMicros={status.data.spentMicros}
                limitMicros={status.data.limitMicros}
                label={he.ai.card.budgetMeter}
                words={he.ai.tenantState}
              />
            </div>
            {canManage ? <BudgetForm tenantId={tenantId} status={status.data} /> : null}
          </div>
        </div>
      )}
    </Card>
  );
}

function Jobs({ jobs }: { jobs: TenantAiStatus['jobs'] }) {
  const rows = [
    ['queued', he.ai.card.jobsQueued],
    ['running', he.ai.card.jobsRunning],
    ['succeeded', he.ai.card.jobsSucceeded],
    ['failed', he.ai.card.jobsFailed],
  ] as const;
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
      {rows.map(([key, label]) => (
        <div key={key} className="contents">
          <dt className="text-slate-500">{label}</dt>
          <dd data-testid={`ai-jobs-${key}`}>{formatCount(jobs[key])}</dd>
        </div>
      ))}
    </dl>
  );
}

function BudgetForm({ tenantId, status }: { tenantId: string; status: TenantAiStatus }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState(status.limitIsDefault ? '' : usdLimitInput(status.limitMicros));
  const [invalid, setInvalid] = useState(false);
  const save = useMutation({
    mutationFn: (monthlyLimitMicros: number | null) =>
      platformApi.setTenantAiBudget(tenantId, monthlyLimitMicros),
    onSuccess: async (result, monthlyLimitMicros) => {
      queryClient.setQueryData(keys.tenantAi(tenantId), result);
      if (monthlyLimitMicros === null) setText('');
      await queryClient.invalidateQueries({ queryKey: keys.ai });
      await queryClient.invalidateQueries({ queryKey: ['audit'] });
    },
  });

  function onSubmit(event: SyntheticEvent) {
    event.preventDefault();
    const micros = parseUsdLimit(text);
    setInvalid(micros === null);
    if (micros === null) {
      save.reset();
      return;
    }
    save.mutate(micros);
  }

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-3">
      <TextField
        label={he.ai.card.limitField}
        name="monthlyLimit"
        inputMode="decimal"
        autoComplete="off"
        dir="ltr"
        maxLength={12}
        value={text}
        error={invalid ? he.ai.card.limitInvalid : undefined}
        hint={he.ai.card.limitHint}
        onChange={(event) => {
          setText(event.target.value);
          setInvalid(false);
          // The answer to the previous amount says nothing about the one being typed.
          if (save.isSuccess || save.isError) save.reset();
        }}
      />
      <div className="flex flex-wrap gap-2">
        <Button type="submit" busy={save.isPending && save.variables !== null}>
          {he.common.save}
        </Button>
        {status.limitIsDefault ? null : (
          <Button
            tone="secondary"
            busy={save.isPending && save.variables === null}
            disabled={save.isPending}
            onClick={() => {
              setInvalid(false);
              save.mutate(null);
            }}
          >
            {he.ai.card.reset}
          </Button>
        )}
      </div>
      {save.isError ? (
        <Alert>
          {errorMessage(save.error, {
            FORBIDDEN: he.ai.card.manageForbidden,
            NOT_FOUND: he.ai.card.notFound,
            VALIDATION_FAILED: he.ai.card.limitInvalid,
          })}
        </Alert>
      ) : null}
      {save.isSuccess ? (
        <Alert tone="success">
          {save.variables === null ? he.ai.card.resetDone : he.ai.card.saved}
        </Alert>
      ) : null}
    </form>
  );
}
